import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, IntegrationTypeEnum } from "~/types/enums";
import { Alert, Card, Grid, Icon, InputCheckbox, InputToggle, Picture, Spinner, Text } from "~/components-shared";
import integrationsActionCreators from "~/store/integrations/actions";
import integrationSelectors from "~/store/integrations/selectors";
import useAbacExt from "~/hooks/abac";
import api from "~/lib/api";
import productboardLogo from "~/assets/i/apps/productboard_logo.svg";
import styled from "styled-components";
import Markdown from "~/components/Markdown";
import { handleError } from "~/lib/helpers";
import lists from "~/lib/lists";
const StyledGrid = styled(Grid)`
  position: relative;
  z-index: 2;
  max-height: 250px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;
const Actions = styled.div`
  position: relative;
  z-index: 2;
`;
const authorities = [AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE, AuthoritiesEnum.SIGN_UP_INTEGRATE];
const ProductboardIntegrationCard = _ref => {
  let {
    ...rest
  } = _ref;
  const {
    userHasOneOf
  } = useAbacExt();
  const dispatch = useDispatch();
  const integration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.PRODUCTBOARD));
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsError, setProductsError] = useState();
  const [customFields, setCustomFields] = useState([]);
  const [loadingCustomFields, setLoadingCustomFields] = useState(false);
  const [customFieldsError, setCustomFieldsError] = useState();
  const [isExpanded, setIsExpanded] = useState(false);
  const error = customFieldsError || productsError;
  const handleLoadProducts = () => {
    setLoadingProducts(true);
    setProductsError(undefined);
    api.fetchProductboardProducts().then(data => setProducts(data)).catch(e => setProductsError(handleError(e))).finally(() => setLoadingProducts(false));
  };
  const handleLoadCustomFields = () => {
    setLoadingCustomFields(true);
    setCustomFieldsError(undefined);
    api.fetchProductboardCustomFields().then(data => setCustomFields(data)).catch(e => setCustomFieldsError(handleError(e))).finally(() => setLoadingCustomFields(false));
  };
  useEffect(() => {
    if (integration && userHasOneOf(authorities)) {
      handleLoadProducts();
      handleLoadCustomFields();
    }
  }, [!!integration]);
  const handleToggle = value => {
    if (value) {
      api.productboardInstall().then(_ref2 => {
        let {
          path
        } = _ref2;
        const width = 560;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        window.open(path, "Install Productboard", `width=${width},height=${height},left=${left},top=${top}`);
      });
      setIsExpanded(true);
    } else if (integration?.id) {
      dispatch(integrationsActionCreators.deleteStarted(integration.id));
    }
  };
  const handleExpand = () => {
    setIsExpanded(prev => !prev);
  };
  const handleUpdateProducts = productId => {
    if (integration) {
      let productIds = integration.settings?.products || [];
      if (productIds.includes(productId)) {
        productIds = productIds.filter(id => id !== productId);
      } else {
        productIds.push(productId);
      }
      dispatch(integrationsActionCreators.updateStarted(integration.id, {
        type: integration.type,
        token: integration.token,
        settings: {
          ...integration.settings,
          products: productIds
        }
      }));
    }
  };
  const handleUpdateCustomFields = customFieldId => {
    if (integration) {
      let customFieldIds = integration.settings?.customFields || [];
      if (customFieldIds.includes(customFieldId)) {
        customFieldIds = customFieldIds.filter(id => id !== customFieldId);
      } else {
        customFieldIds.push(customFieldId);
      }
      dispatch(integrationsActionCreators.updateStarted(integration.id, {
        type: integration.type,
        token: integration.token,
        settings: {
          ...integration.settings,
          customFields: customFieldIds
        }
      }));
    }
  };
  return /*#__PURE__*/React.createElement(Card, _extends({
    onClick: userHasOneOf(authorities) && integration ? handleExpand : undefined,
    elemBefore: /*#__PURE__*/React.createElement(Picture, {
      src: productboardLogo,
      width: "30px",
      height: "30px",
      border: "squared",
      color: "transparent",
      s: {
        mr: 2
      }
    }),
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      display: "inline-block"
    }, "Productboard"), integration && userHasOneOf(authorities) && /*#__PURE__*/React.createElement(Icon, {
      scale: 4,
      src: "chevron",
      rotate: isExpanded ? 0 : 180,
      s: {
        ml: 1,
        mb: 1
      }
    })),
    elemAfter: /*#__PURE__*/React.createElement(Actions, null, /*#__PURE__*/React.createElement(InputToggle, {
      value: !!integration,
      display: "inline-block",
      onChange: (value, event) => {
        event.stopPropagation();
        handleToggle(value);
      },
      disabled: !userHasOneOf(authorities)
    })),
    collapsed: !integration || !isExpanded,
    s: {
      mb: 3
    }
  }, rest), /*#__PURE__*/React.createElement(Markdown, {
    display: "block",
    fontSize: 4,
    lineHeight: 6,
    linkColor: "primary",
    s: {
      mb: 3
    },
    text: `Connected to [${integration?.settings?.workspace?.space?.name}](https://${integration?.settings?.workspace?.space?.domain}.productboard.com/) Space by ${integration?.settings?.workspace?.resource_owner?.name} (${integration?.settings?.workspace?.resource_owner?.email}). Select Products:`
  }), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), loadingProducts ? /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }) : products.length ? /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 2,
    template: "1rem 1fr auto",
    s: {
      p: 2,
      mb: 5
    },
    gap: 5,
    fullWidth: true
  }, products.map(product => /*#__PURE__*/React.createElement(React.Fragment, {
    key: product.id
  }, /*#__PURE__*/React.createElement(InputCheckbox, {
    value: integration?.settings?.products?.includes(product.id),
    onChange: () => handleUpdateProducts(product.id),
    s: {
      mt: -1.8
    }
  }), /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    display: "block",
    weight: "semiBold"
  }, product.name), /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    display: "block",
    align: "right"
  }, product.owner || "No owner")))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary"
  }, "No Teams"), /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    s: {
      mb: 4
    },
    display: "block"
  }, "Select Custom Fields you want to track:"), loadingCustomFields ? /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }) : customFields.length ? /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 3,
    template: "1rem 1fr auto",
    s: {
      p: 2,
      mb: 2
    },
    gap: 5,
    fullWidth: true
  }, customFields.map(customField => /*#__PURE__*/React.createElement(React.Fragment, {
    key: customField.id
  }, /*#__PURE__*/React.createElement(InputCheckbox, {
    value: integration?.settings?.customFields?.includes(customField.id),
    onChange: () => handleUpdateCustomFields(customField.id),
    s: {
      mt: -1.8
    }
  }), /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    display: "block",
    weight: "semiBold"
  }, customField.name), /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    display: "block",
    align: "right"
  }, lists.productboard.customFieldTypes.names[customField.type])))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary"
  }, "No Custom Fields"));
};
export default ProductboardIntegrationCard;